import { useState } from "react";
import { useForm } from "react-hook-form";
import { House, Lock, User } from "lucide-react";
import * as accountApi from "../../../api/account/api";
import { AuthStatus } from "../../../types/domain";
import { CapsLockWarning } from "../../../components/common/CapsLockWarning";
import { ErrorAlert } from "../components/ErrorAlert";
import { SubmitButton } from "../components/SubmitButton";
import { LoginFormField } from "../components/LoginFormField";
import { MicrosoftIcon } from "../../../components/logos/MicrosoftIcon";
import { PageLoader } from "../components/PageLoader";
import { FormOverlay } from "@/components/common/Loading";
import { PasswordVisibilityToggle } from "@/components/common/PasswordVisibilityToggle";
import { ToggleSwitch } from "../components/ToggleSwitch";
import { LoginViewState } from "../types";
import { useApiError } from "../hooks/useApiError";
import { useCapsLock } from "../../../hooks/useCapsLock";
import { usePasswordVisibility } from "../../../hooks/usePasswordVisibility";
import { PASSWORD_MAX_INPUT_LENGTH, safeRedirectPath, validatePasswordLength } from "../utils/validators";
import { getBaseUtcOffset } from "../utils/timezone";
import { ViewHeading } from "../components/ViewHeading";

type Props = {
  initialAccount?: string;
  onViewChange: (view: LoginViewState, extra?: Record<string, string>) => void;
};

type LoginFormValues = {
  account: string;
  username: string;
  password: string;
};

export const LoginView = ({ initialAccount = "", onViewChange }: Props) => {
  const { showPass, toggle: toggleShowPass } = usePasswordVisibility();
  const { capsLockOn, onKeyDown: onCapsKeyDown, onKeyUp: onCapsKeyUp } = useCapsLock();
  const { apiError, setApiError, clearError } = useApiError();
  const [customerMode, setCustomerMode] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    defaultValues: { account: initialAccount, username: "", password: "" },
  });

  const onSubmit = async (data: LoginFormValues) => {
    clearError();
    try {
      const result = await accountApi.login({
        account: data.account.trim(),
        username: data.username.trim(),
        password: data.password,
        customerMode,
        timezone: getBaseUtcOffset(),
        returnUrl: new URLSearchParams(window.location.search).get("ReturnUrl") ?? "",
      });

      switch (result.status) {
        case AuthStatus.Success:
          setRedirecting(true);
          window.location.href = safeRedirectPath(result.redirectUrl);
          break;
        case AuthStatus.Requires2Fa:
          onViewChange("verifyCode");
          break;
        case AuthStatus.RequiresPasswordUpdate:
          onViewChange("updatePassword", {
            passwordDays: String(result.passwordDays ?? 0),
            passwordStatus: result.passwordStatus ?? "",
          });
          break;
        case AuthStatus.Error:
        default:
          setApiError(result.errorMessage ?? "An error occurred. Please try again.");
      }
    } catch {
      setApiError("An unexpected error occurred. Please try again.");
    }
  };

  const handleMicrosoft = async () => {
    const account = getValues("account");
    if (!account.trim()) {
      onViewChange("accountSelection", { customerMode: customerMode.toString() });
      return;
    }
    clearError();
    setSsoLoading(true);
    try {
      const { authUrl } = await accountApi.ssoInitiate({
        account: account.trim(),
        customerMode,
        timezone: getBaseUtcOffset(),
      });
      window.location.href = authUrl;
    } catch {
      setApiError("Unable to initiate Microsoft sign-in. Please try again.");
      setSsoLoading(false);
    }
  };

  return (
    <>
      {redirecting && <PageLoader />}

      <FormOverlay loading={isSubmitting || redirecting || ssoLoading}>

      <ViewHeading
        title="Welcome back"
        subtitle="Sign in to your Klipboard CRM account"
        titleClassName="text-[clamp(1.2rem,2vw,2.25rem)]"
        subtitleClassName="mb-4 lg:mb-7"
      />

      {apiError && <ErrorAlert message={apiError} />}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <LoginFormField
          label="Account"
          placeholder="Your account"
          autoComplete="organization"
          error={errors.account?.message}
          disabled={isSubmitting}
          icon={<House size={16} color="#9CA3AF" strokeWidth={2} />}
          {...register("account", { required: "Account is required" })}
        />

        <LoginFormField
          label="Username"
          placeholder="Your username"
          autoComplete="username"
          error={errors.username?.message}
          disabled={isSubmitting}
          icon={<User size={16} color="#9CA3AF" strokeWidth={2} />}
          {...register("username", { required: "Username is required" })}
        />

        <LoginFormField
          label="Password"
          type={showPass ? "text" : "password"}
          placeholder="Your password"
          autoComplete="current-password"
          error={errors.password?.message}
          disabled={isSubmitting}
          icon={<Lock size={16} color="#9CA3AF" strokeWidth={2} />}
          onKeyDown={onCapsKeyDown}
          onKeyUp={onCapsKeyUp}
          trailingElement={<PasswordVisibilityToggle showPass={showPass} onToggle={toggleShowPass} />}
          maxLength={PASSWORD_MAX_INPUT_LENGTH}
          {...register("password", {
            required: "Password is required",
            validate: (value) => validatePasswordLength(value),
          })}
        />

        <CapsLockWarning visible={capsLockOn} />

        <ToggleSwitch
          checked={customerMode}
          onChange={setCustomerMode}
          label="Customer Privacy Mode"
          description="Hides sensitive sales data when presenting to a customer"
        />

        {/* Forgot password */}
        <div className="text-right mb-3 lg:mb-5">
          <button
            type="button"
            onClick={() => onViewChange("forgotPassword")}
            className="text-[0.8125rem] font-medium text-brand-pink bg-transparent border-none cursor-pointer p-0"
          >
            Forgot password?
          </button>
        </div>

        <SubmitButton loading={isSubmitting}>Sign in</SubmitButton>
      </form>

      <div className="flex items-center gap-3 my-3 lg:my-5">
        <div className="flex-1 h-px bg-[#E5E7EB]" />
        <span className="text-[0.75rem] text-[#9CA3AF] whitespace-nowrap">Or continue with</span>
        <div className="flex-1 h-px bg-[#E5E7EB]" />
      </div>

      {/* Microsoft */}
      <button
        type="button"
        onClick={handleMicrosoft}
        disabled={ssoLoading || isSubmitting}
        className="w-full flex items-center justify-center gap-2.5 py-3 rounded-lg border border-[#E5E7EB] bg-white cursor-pointer text-[0.875rem] font-medium text-[#374151] mb-4 lg:mb-6 transition-colors hover:bg-[#F3F4F6] disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {ssoLoading ? (
          <>
            <div className="w-4 h-4 border-2 border-[#374151]/20 border-t-[#374151] rounded-full animate-spin" />
            Redirecting…
          </>
        ) : (
          <>
            <MicrosoftIcon />
            Sign in with Microsoft 365
          </>
        )}
      </button>

      <p className="text-center text-[0.8125rem] text-[#6B7280] m-0">
        New to Klipboard CRM?{" "}
        <a href="https://vecta.net/en-gb/contact-us" target="_blank" rel="noopener noreferrer" className="text-brand-pink font-medium no-underline">
          Request access
        </a>
      </p>
      </FormOverlay>
    </>
  );
};
